import { ArrowRight } from "lucide-react";
import { Reveal, Label } from "./Reveal";
import { CALENDLY_URL } from "../lib/contact";
import { trackEvent } from "../lib/tracking";

// "What happens after we talk?" — the working-together process, placed right
// after MeetAndrea so it answers "what am I actually getting?" the moment the
// visitor knows who Andrea is. Ends with the primary booking CTA.
const STEPS = [
  {
    n: "01",
    title: ["Reserve your complimentary", "sleep assessment."],
    body: "A real conversation, not a sales call. You tell me what’s been happening, I ask a few questions, and you leave knowing what a next step could look like — whether or not we ever work together.",
  },
  {
    n: "02",
    title: ["We talk through what’s", "actually happening."],
    body: "First, I listen. What nights look like, what naps look like, what’s been hardest — and what you’ve already tried, even the things that only worked for a little while.",
  },
  {
    n: "03",
    title: ["You receive a", "personal recommendation."],
    body: "Based on your child, their stage, and your family: what I’d suggest, why, and what working together would look like. Not a generic sleep schedule or a one-size-fits-all program — and the decision is always yours.",
  },
  {
    n: "04",
    title: ["Support that adapts", "to real life."],
    body: "Because sleep doesn’t always follow the plan: a nap changes, bedtime goes differently, something that worked yesterday suddenly doesn’t work tonight. If tomorrow looks completely different, tell me what happened and we’ll look at it again. You won’t be back at Google, starting from zero. Andrea is still there.",
  },
];

export const Process = () => (
  <section id="how-it-works" data-testid="process-section" className="scroll-mt-24 bg-ivory">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-24 lg:py-36 grid grid-cols-1 lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-28">
          <Reveal>
            <Label>Working together</Label>
          </Reveal>
          <Reveal as="h2" delay={0.08} className="mt-6 font-serif font-light text-ink text-4xl sm:text-5xl leading-[1.08] tracking-tight">
            What happens
            <span className="block italic text-clay">after we talk?</span>
          </Reveal>
        </div>
      </div>

      <div className="lg:col-span-6 lg:col-start-7">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={0.06 * i}>
            <div data-testid={`process-step-${i}`} className="py-8 lg:py-10 border-t border-ink/10 last:border-b">
              <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-euca">{s.n}</p>
              <p className="mt-3 font-serif font-light text-ink text-2xl sm:text-3xl leading-snug tracking-tight">
                {s.title[0]}
                <span className="block italic text-clay">{s.title[1]}</span>
              </p>
              <p className="mt-4 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-xl">
                {s.body}
              </p>
            </div>
          </Reveal>
        ))}

        <Reveal delay={0.2}>
          <p className="mt-12 font-serif font-light text-ink text-3xl sm:text-4xl leading-[1.15] tracking-tight">
            Start with a
            <span className="italic text-clay"> complimentary pediatric sleep assessment.</span>
          </p>
          <a
            data-testid="process-book-cta"
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent("cta_click", { location: "process" });
              trackEvent("book_assessment_click", { location: "process" });
            }}
            className="mt-7 w-full sm:w-auto justify-center inline-flex items-center gap-3 rounded-full bg-ink text-ivory font-medium px-8 py-4 text-base hover:bg-espresso transition-colors duration-300 group"
          >
            Book my complimentary pediatric sleep assessment
            <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);
